import { useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Tooltip } from '../../../components/Tooltip';
import type { Company } from '../../../domain/company';
import { OpportunityListTypeEnum } from '../../../domain/list';
import { useOpportunityListQuery } from '../../../features/opportunityLists/opportunityListQuery/useOpportunityListQuery';
import { translate } from '../../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { StyledDropdown, StyledInfoButton, StyledInfoButtonWrapper } from '../CompanyActionsContainer/styles';

import { StyledListDropdownOption } from './styles';

const STATIC_LIST_URL = 'https://pro.similarweb.com/#/sales/lists/static/';

export const SavedListsInfoContainer = ({ company }: { company: Company }) => {
  const { domain, name: companyName } = company;
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const { data } = useOpportunityListQuery();

  const handleClickListButton = () => setIsDropdownOpened(true);

  const handleCloseDropdown = () => setIsDropdownOpened(false);

  const handleTrackOpenStaticList = (listName: string) =>
    dispatchTrackEventWithLocation(
      'company data',
      'open static list',
      `view company in static list/${listName}/${companyName}`
    );

  const listsContainingDomain = data?.filter((list) => list.domains.includes(domain));

  if (!listsContainingDomain || listsContainingDomain?.length === 0) {
    return null;
  }

  const firstList = listsContainingDomain[0];

  return (
    <StyledInfoButtonWrapper data-automation="SavedListWrapper">
      {listsContainingDomain.length === 1 ? (
        <Tooltip
          placement="top"
          content={translate('company.save_to_list.view_list_name', { listName: firstList.name })}
          minWidth={120}
          maxWidth={200}
        >
          <StyledInfoButton data-automation="SavedListButton">
            <a
              href={`${STATIC_LIST_URL}${firstList.id}`}
              target="_blank"
              rel="noreferrer"
              onMouseDown={() => handleTrackOpenStaticList(firstList.name)}
            >
              <SWReactIcons iconName="similar-icon-bw" size="xs" />
            </a>
          </StyledInfoButton>
        </Tooltip>
      ) : (
        <>
          <Tooltip
            placement="top"
            content={translate('company.save_to_list.view_amount_lists', { amount: listsContainingDomain.length })}
            minWidth={100}
          >
            <StyledInfoButton
              onClick={handleClickListButton}
              isActive={isDropdownOpened}
              data-automation="SavedListButton2"
            >
              <SWReactIcons iconName="similar-icon-bw" size="xs" />
            </StyledInfoButton>
          </Tooltip>
          <StyledDropdown
            isOpened={isDropdownOpened}
            onClose={handleCloseDropdown}
            minWidth={234}
            placement="right"
            data-automation="SavedListDd"
          >
            {listsContainingDomain.map(({ id, name, type }) => (
              <a
                href={`${STATIC_LIST_URL}${id}`}
                key={id}
                target="_blank"
                rel="noreferrer"
                onMouseDown={() => handleTrackOpenStaticList(name)}
              >
                <StyledListDropdownOption data-automation="SavedListDdOption">
                  <SWReactIcons
                    iconName={type === OpportunityListTypeEnum.Shared ? 'assign-to-user' : 'snippet-list'}
                    iconColor={colorsPalettes.carbon[200]}
                  />
                  {name}
                </StyledListDropdownOption>
              </a>
            ))}
          </StyledDropdown>
        </>
      )}
    </StyledInfoButtonWrapper>
  );
};
